import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import {
  type SiteSeparation as SiteSeparationSettings,
  siteSeparationSchemaZ,
} from '@ipfabric/utils/schema/siteSeparation/siteSeparation';
import type { SiteSeparationRule } from '@ipfabric/utils/schema/siteSeparation/siteSeparationRule';
import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import type { DiscoverySettingsGroupDisplayControl } from '../../../composition/DiscoverySettings/DiscoverySettings';
import type { DiscoverySettingsObject } from '../../../model/DiscoverySettingsObject';
import { getSiteSeparationWithDefaults } from '../siteSeparationDefaults';
import { RuleEditorSidePanel } from './RuleEditorSidePanel';
import { RuleListTable } from './RuleListTable';

type RuleListFieldValues = {
  siteSeparation: SiteSeparationSettings;
};

export const upsertRule = (
  rules: SiteSeparationRule[],
  ruleToSave: SiteSeparationRule,
): SiteSeparationRule[] => {
  const existingIndex = rules.findIndex((rule) => rule.id === ruleToSave.id);
  if (existingIndex < 0) {
    return [...rules, ruleToSave];
  }
  return rules.map((rule) => (rule.id === ruleToSave.id ? ruleToSave : rule));
};

type RuleListFormAdapterProps = {
  form: UseFormReturn<RuleListFieldValues>;
  goToAuthenticationVendors?: () => void;
};

const RuleListFormAdapter = (props: RuleListFormAdapterProps) => {
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [editedRuleId, setEditedRuleId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'edit' | 'test'>('edit');
  const siteSeparation = useWatch({
    control: props.form.control,
    name: 'siteSeparation',
  });
  const rules = siteSeparation?.rules ?? [];
  const editedRule = rules.find((rule) => rule.id === editedRuleId);
  const hasRoutingSwitchingRule = rules.some(
    ({ type, id }) => type === 'routingSwitching' && id !== editedRule?.id,
  );

  const closeEditor = () => {
    setIsAddPanelOpen(false);
    setEditedRuleId(null);
    setPanelMode('edit');
  };

  return (
    <>
      <RuleListTable
        rules={rules}
        onRulesChange={(reorderedRules) => {
          props.form.setValue('siteSeparation.rules', reorderedRules, {
            shouldDirty: true,
          });
        }}
        onAddRuleClick={() => {
          setEditedRuleId(null);
          setIsAddPanelOpen(true);
          setPanelMode('edit');
        }}
        onEditRule={(rule) => {
          setIsAddPanelOpen(false);
          setEditedRuleId(rule.id);
          setPanelMode('edit');
        }}
        onRemoveRule={(ruleId) => {
          props.form.setValue(
            'siteSeparation.rules',
            rules.filter(({ id }) => id !== ruleId),
            { shouldDirty: true },
          );
        }}
        onTestRule={(rule) => {
          setIsAddPanelOpen(false);
          setEditedRuleId(rule.id);
          setPanelMode('test');
        }}
      />
      {(isAddPanelOpen || editedRule) && (
        <RuleEditorSidePanel
          rule={editedRule}
          hasRoutingSwitchingRule={hasRoutingSwitchingRule}
          initialMode={panelMode}
          goToAuthenticationVendors={props.goToAuthenticationVendors}
          onClose={closeEditor}
          onSubmit={(rule) => {
            const nextRules = isAddPanelOpen
              ? [...rules, rule]
              : upsertRule(rules, rule);
            props.form.setValue('siteSeparation.rules', nextRules, {
              shouldDirty: true,
            });
            closeEditor();
          }}
        />
      )}
    </>
  );
};

export const ruleList: FormSnippet<
  DiscoverySettingsObject,
  RuleListFieldValues,
  undefined,
  void,
  DiscoverySettingsGroupDisplayControl
> = {
  render: ({ form, groupDisplayControl }) => (
    <RuleListFormAdapter
      form={form}
      goToAuthenticationVendors={() => {
        groupDisplayControl?.setTab('authentication');
        groupDisplayControl?.setAuthenticationSubTab('vendors');
      }}
    />
  ),
  fromModel: (model, fieldValuesDraft) => {
    fieldValuesDraft.siteSeparation = getSiteSeparationWithDefaults(
      model.siteSeparation,
    );
  },
  toModel: (transformedValues, modelDraft) => {
    modelDraft.siteSeparation = {
      ...getSiteSeparationWithDefaults(modelDraft.siteSeparation),
      rules: transformedValues.siteSeparation.rules,
    };
  },
  schema: z.object({
    siteSeparation: siteSeparationSchemaZ,
  }),
};
